/*
 * Montana Fruit Trees: "Notify When Back in Stock" waitlist
 * - Opens the shared popup (snippets/back-in-stock-modal.liquid) from any [data-bis-open] button
 * - Keeps the product-page button in sync with the selected variant (Horizon 'variant:update' event)
 * - Sends each signup to the Google Apps Script waitlist AND Shopify's newsletter form
 * - Pollination map pages: adds a "Join the waitlist" button to sold-out partner cards (a.mftm-p.is-out)
 * - Plant Finder results (collection filters + finder page): same button on sold-out cards (li.mftf-card.is-out)
 */
(() => {
  if (window.__mftBackInStock) return;
  window.__mftBackInStock = true;

  const dialog = document.getElementById('BackInStockDialog');
  if (!dialog) return;

  const $ = (sel, root = dialog) => root.querySelector(sel);
  const form = $('[data-bis-form]');
  const errorEl = $('[data-bis-error]');
  const submitBtn = $('[data-bis-submit]');
  const variantField = $('[data-bis-variant-field]');
  const variantSelect = $('[data-bis-variant-select]');
  const endpoint = (dialog.dataset.endpoint || '').trim();
  const STORAGE_KEY = 'mft-bis-contact';
  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

  let current = null; // data for the product being signed up for
  let lastTrigger = null;

  const storage = {
    get() {
      try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null'); } catch (e) { return null; }
    },
    set(v) {
      try { localStorage.setItem(STORAGE_KEY, JSON.stringify(v)); } catch (e) { /* ignore */ }
    },
  };

  function readVariants(button) {
    const wrap = button.closest('[data-bis-trigger]');
    const script = wrap && wrap.querySelector('script[data-bis-variants]');
    if (!script) return [];
    try { return JSON.parse(script.textContent) || []; } catch (e) { return []; }
  }

  function cleanTitle(t) {
    return t && t !== 'Default Title' ? t : '';
  }

  function showView(name) {
    dialog.querySelectorAll('[data-bis-view]').forEach((v) => { v.hidden = v.dataset.bisView !== name; });
  }

  function setError(msg) {
    errorEl.textContent = msg || '';
    errorEl.hidden = !msg;
  }

  function openFor(button) {
    lastTrigger = button;
    const d = button.dataset;
    const variants = readVariants(button);
    current = {
      product_id: d.productId,
      product_title: d.productTitle,
      product_handle: d.productHandle,
      product_url: d.productUrl,
      product_image: d.productImage,
      variant_id: d.variantId || '',
      variant_title: cleanTitle(d.variantTitle),
      context: d.context || 'product',
    };

    // Product header
    $('[data-bis-product-title]').textContent = current.product_title || '';
    $('[data-bis-variant-title]').textContent = current.variant_title || '';
    const img = $('[data-bis-image]');
    if (current.product_image) { img.src = current.product_image; img.hidden = false; } else { img.hidden = true; }

    // Variant chooser (product cards: product is fully sold out, let them pick which size/rootstock)
    variantSelect.innerHTML = '';
    const soldOut = variants.filter((v) => !v.available);
    if (!current.variant_id) {
      if (variants.length <= 1) {
        const only = variants[0];
        current.variant_id = only ? String(only.id) : 'any';
        current.variant_title = only ? cleanTitle(only.title) : '';
        $('[data-bis-variant-title]').textContent = current.variant_title;
        variantField.hidden = true;
      } else {
        const anyOpt = new Option('Any size / option (first one back)', 'any');
        variantSelect.add(anyOpt);
        (soldOut.length ? soldOut : variants).forEach((v) => variantSelect.add(new Option(cleanTitle(v.title) || 'Default', String(v.id))));
        variantField.hidden = false;
      }
    } else {
      variantField.hidden = true;
    }

    // Prefill from last signup on this device
    const saved = storage.get();
    if (saved) {
      form.first_name.value ||= saved.first_name || '';
      form.last_name.value ||= saved.last_name || '';
      form.email.value ||= saved.email || '';
    }

    setError('');
    submitBtn.removeAttribute('aria-busy');
    submitBtn.disabled = false;
    showView('form');
    if (typeof dialog.showModal === 'function') dialog.showModal(); else dialog.setAttribute('open', '');
    const firstEmpty = [form.first_name, form.last_name, form.email].find((i) => !i.value);
    (firstEmpty || submitBtn).focus();
  }

  function close() {
    if (dialog.open) dialog.close();
  }

  dialog.addEventListener('close', () => {
    if (lastTrigger && document.contains(lastTrigger)) lastTrigger.focus({ preventScroll: true });
  });

  // Close on backdrop click
  dialog.addEventListener('click', (e) => {
    if (e.target === dialog) close();
    if (e.target.closest('[data-bis-close]')) close();
  });

  // Open: capture phase so product-card links / card click handlers never see the click
  document.addEventListener(
    'click',
    (e) => {
      const btn = e.target.closest('[data-bis-open]');
      if (!btn) return;
      e.preventDefault();
      e.stopPropagation();
      openFor(btn);
    },
    true
  );

  // Keep the product-page button in sync with the selected variant (Horizon theme event)
  document.addEventListener('variant:update', (e) => {
    const detail = e.detail || {};
    const data = detail.data || {};
    const productId = String((data.newProduct && data.newProduct.id) || data.productId || '');
    const variant = detail.resource;
    document.querySelectorAll('[data-bis-trigger][data-bis-context="product"]').forEach((wrap) => {
      if (productId && wrap.dataset.productId !== productId) return;
      const btn = wrap.querySelector('[data-bis-open]');
      if (!variant) { wrap.hidden = true; return; } // option combination doesn't exist
      if (btn) {
        btn.dataset.variantId = String(variant.id);
        btn.dataset.variantTitle = cleanTitle(variant.title || variant.public_title || '');
      }
      wrap.hidden = !!variant.available;
    });
  });

  async function sendToWaitlist(payload) {
    const body = JSON.stringify(payload);
    try {
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'text/plain;charset=utf-8' }, // "simple" request: no CORS preflight
        body,
        redirect: 'follow',
      });
      const json = await res.json().catch(() => ({ ok: res.ok }));
      if (json && json.ok === false) throw Object.assign(new Error(json.error || 'Signup failed'), { fromServer: true });
      return true;
    } catch (err) {
      if (err.fromServer) throw err;
      // Response unreadable (CORS) — resend opaquely; the server de-duplicates email + variant.
      await fetch(endpoint, { method: 'POST', mode: 'no-cors', headers: { 'Content-Type': 'text/plain;charset=utf-8' }, body });
      return true;
    }
  }

  function subscribeToNewsletter(p) {
    const fd = new FormData();
    fd.append('form_type', 'customer');
    fd.append('utf8', '✓');
    fd.append('contact[email]', p.email);
    fd.append('contact[first_name]', p.first_name);
    fd.append('contact[last_name]', p.last_name);
    fd.append('contact[tags]', 'newsletter, back-in-stock');
    return fetch('/contact', { method: 'POST', body: fd, credentials: 'same-origin' }).catch(() => null);
  }

  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    setError('');
    const first = form.first_name.value.trim();
    const last = form.last_name.value.trim();
    const email = form.email.value.trim().toLowerCase();
    [form.first_name, form.last_name, form.email].forEach((i) => i.removeAttribute('aria-invalid'));

    if (!first) { form.first_name.setAttribute('aria-invalid', 'true'); form.first_name.focus(); return setError('Please enter your first name.'); }
    if (!last) { form.last_name.setAttribute('aria-invalid', 'true'); form.last_name.focus(); return setError('Please enter your last name.'); }
    if (!EMAIL_RE.test(email)) { form.email.setAttribute('aria-invalid', 'true'); form.email.focus(); return setError('Please enter a valid email address.'); }
    if (!current) return;

    let variantId = current.variant_id;
    let variantTitle = current.variant_title;
    if (!variantField.hidden) {
      variantId = variantSelect.value;
      variantTitle = variantId === 'any' ? 'Any size / option' : variantSelect.options[variantSelect.selectedIndex].text;
    }

    const payload = {
      ...current,
      variant_id: variantId,
      variant_title: variantTitle,
      first_name: first,
      last_name: last,
      email,
      newsletter: true,
      page_url: location.href,
      company: form.company.value, // honeypot
    };

    // Bots fill the hidden field; pretend success and do nothing.
    if (payload.company) return finish(payload);

    if (!endpoint) {
      return setError('Sorry, waitlist signups are not available right now. Please email us and we’ll add you.');
    }

    submitBtn.disabled = true;
    submitBtn.setAttribute('aria-busy', 'true');
    submitBtn.textContent = 'Adding you…';
    try {
      await Promise.all([sendToWaitlist(payload), subscribeToNewsletter(payload)]);
      storage.set({ first_name: first, last_name: last, email });
      finish(payload);
    } catch (err) {
      setError('Something went wrong. Please try again in a moment.');
    } finally {
      submitBtn.disabled = false;
      submitBtn.removeAttribute('aria-busy');
      submitBtn.textContent = 'Notify Me';
    }
  });

  function finish(p) {
    const what = p.variant_title && p.variant_id !== 'any' ? `${p.product_title} (${p.variant_title})` : p.product_title;
    $('[data-bis-success-text]').textContent = `Thanks, ${p.first_name}! We’ll email ${p.email} as soon as ${what} is back in stock.`;
    showView('success');
    const done = dialog.querySelector('[data-bis-view="success"] [data-bis-close]');
    if (done) done.focus();
  }

  // ── Sold-out cards built in page JS ─────────────────────────
  // Two places build product cards in JS, so the theme's own product-card markup never runs there:
  //  1. The partner finder on each /pages/*-pollination-map page (a.mftm-p, sold out = .is-out).
  //     The "Sold out" chip is swapped for the waitlist button.
  //  2. The Plant Finder results on collection pages and the finder page (li.mftf-card, sold out = .is-out).
  //     The waitlist button goes right under the price.
  // Either way shoppers can join the waitlist from the card without opening the product page.
  // A product tagged "no-waitlist" never gets the button.
  (function soldOutCards() {
    const MAP_SEL = '[data-mftm-finder] a.mftm-p.is-out:not([data-bis-done])';
    const FINDER_SEL = '[data-mftf] li.mftf-card.is-out:not([data-bis-done])';
    const HIDE_TAG = 'no-waitlist';
    const BELL = '<svg viewBox="0 0 20 20" width="14" height="14" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M10 2.5a5 5 0 0 0-5 5v3.2L3.5 13.5h13L15 10.7V7.5a5 5 0 0 0-5-5Z"/><path d="M8.2 16a1.9 1.9 0 0 0 3.6 0"/></svg>';
    const cache = {};

    function addStyles() {
      const style = document.createElement('style');
      style.textContent =
        '.mftm-p.is-out:has(.mftm-bis){opacity:1}' +
        '.mftm-p.is-out:has(.mftm-bis) .mftm-p__img{opacity:.72}' +
        '.mftm-bis{display:block;margin-top:2px}' +
        '.mftf-card.is-out:has(.mftf-bis){opacity:1}' +
        '.mftf-card.is-out:has(.mftf-bis) .mftf-card__img img{opacity:.72}' +
        '.mftf-bis{display:block;margin:8px 0 2px}' +
        '.mftm-wait{display:inline-flex;align-items:center;gap:6px;padding:5px 12px;min-height:34px;border:1.5px solid var(--mftm-good,var(--f-green,#2f5e2a));border-radius:999px;background:#fff;color:var(--mftm-good,var(--f-green,#2f5e2a));font:inherit;font-size:14px;font-weight:700;line-height:1.2;cursor:pointer;transition:background-color .15s,color .15s}' +
        '.mftm-wait:hover,.mftm-wait:focus-visible{background:var(--mftm-good,var(--f-green,#2f5e2a));color:#fff}' +
        '.mftm-wait:focus-visible{outline:2px solid currentColor;outline-offset:2px}';
      document.head.appendChild(style);
    }

    function getProduct(handle) {
      if (!cache[handle]) {
        cache[handle] = fetch('/products/' + encodeURIComponent(handle) + '.js', { credentials: 'same-origin' })
          .then((r) => (r.ok ? r.json() : null))
          .catch(() => null);
      }
      return cache[handle];
    }

    function smallImg(u) {
      if (!u) return '';
      if (u.startsWith('//')) u = 'https:' + u;
      return u + (u.includes('?') ? '&' : '?') + 'width=240';
    }

    function trigger(p, wrapClass) {
      const wrap = document.createElement('span');
      wrap.className = wrapClass;
      wrap.setAttribute('data-bis-trigger', '');
      wrap.dataset.bisContext = 'card';
      wrap.dataset.productId = String(p.id);

      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'mftm-wait';
      btn.setAttribute('data-bis-open', '');
      btn.dataset.productId = String(p.id);
      btn.dataset.productTitle = p.title || '';
      btn.dataset.productHandle = p.handle || '';
      btn.dataset.productUrl = location.origin + '/products/' + (p.handle || '');
      btn.dataset.productImage = smallImg(p.featured_image);
      btn.dataset.variantId = '';
      btn.dataset.variantTitle = '';
      btn.dataset.context = 'card';
      btn.setAttribute('aria-label', 'Join the waitlist for ' + (p.title || 'this tree'));
      btn.innerHTML = BELL + '<span>Join the waitlist</span>';

      const data = document.createElement('script');
      data.type = 'application/json';
      data.setAttribute('data-bis-variants', '');
      data.textContent = JSON.stringify((p.variants || []).map((v) => ({ id: v.id, title: v.title, available: v.available })));

      wrap.append(btn, data);
      return wrap;
    }

    // linkEl: element whose href points at the product. place(p): puts the button on the card.
    function enhance(card, linkEl, place) {
      if (card.dataset.bisDone) return;
      card.dataset.bisDone = '1';
      const m = ((linkEl && linkEl.getAttribute('href')) || '').match(/\/products\/([^/?#]+)/);
      if (!m) return;
      getProduct(decodeURIComponent(m[1])).then((p) => {
        if (!p || !document.contains(card)) return;
        if ((p.tags || []).indexOf(HIDE_TAG) !== -1) return;
        place(p);
      });
    }

    function scan() {
      document.querySelectorAll(MAP_SEL).forEach((card) => {
        const slot = card.querySelector('.mftm-p__price');
        if (!slot) { card.dataset.bisDone = '1'; return; }
        enhance(card, card, (p) => slot.replaceChildren(trigger(p, 'mftm-bis')));
      });
      document.querySelectorAll(FINDER_SEL).forEach((card) => {
        const price = card.querySelector('.mftf-card__price');
        if (!price) { card.dataset.bisDone = '1'; return; }
        enhance(card, card.querySelector('a.mftf-card__title'), (p) => {
          if ((p.variants || []).some((v) => v.available)) return; // restocked since the finder data was cached
          price.after(trigger(p, 'mftf-bis'));
        });
      });
    }

    // Wait for the DOM before checking for finder markup, in case this script ever loads ahead of it.
    function start() {
      if (!document.querySelector('[data-mftm-finder], [data-mftf]')) return;
      addStyles();
      let queued = false;
      new MutationObserver(() => {
        if (queued) return;
        queued = true;
        // setTimeout, not requestAnimationFrame: rAF never fires in a background tab, so a card list that
        // finished loading while the tab was hidden would never get its buttons.
        setTimeout(() => { queued = false; scan(); }, 30);
      }).observe(document.body, { childList: true, subtree: true });
      scan();
    }
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
    else start();
  })();
})();